import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import { triggerRevalidation } from '@/lib/utils'
import type { BlockConfig } from '@/hooks/use-homepage'
import type { Json } from '@/types/supabase'

export interface TenantPage {
  id: string
  tenant_id: string
  slug: string
  title: string
  is_system: boolean
  sections: BlockConfig[]
  is_active: boolean
  created_at: string
  updated_at: string
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export function usePages() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['tenant-pages', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenant_pages')
        .select('*')
        .eq('tenant_id', tenantId!)
        .eq('is_active', true)
        .order('slug')
      if (error) throw error
      return data as unknown as TenantPage[]
    },
  })
}

export function usePageBySlug(slug: string) {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['tenant-page', tenantId, slug],
    enabled: !!tenantId && !!slug,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenant_pages')
        .select('*')
        .eq('tenant_id', tenantId!)
        .eq('slug', slug)
        .single()
      if (error) throw error
      return data as unknown as TenantPage
    },
  })
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function useCreatePage() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async ({
      slug,
      title,
      is_system = false,
      initialSections,
    }: {
      slug: string
      title: string
      is_system?: boolean
      initialSections?: BlockConfig[]
    }) => {
      const { data, error } = await supabase
        .from('tenant_pages')
        .insert({
          tenant_id: tenantId!,
          slug: slug.toLowerCase().trim(),
          title: title.trim(),
          is_system,
          sections: (initialSections ?? []) as unknown as Json,
          is_active: true,
        })
        .select()
        .single()
      if (error) throw error
      return data as unknown as TenantPage
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['tenant-pages', tenantId] })
      await triggerRevalidation('tenant-pages')
    },
  })
}

export function useUpdatePageSections(slug: string) {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (sections: BlockConfig[]) => {
      const { data, error } = await supabase
        .from('tenant_pages')
        .update({ sections: sections as unknown as Json })
        .eq('tenant_id', tenantId!)
        .eq('slug', slug)
        .select()
        .single()
      if (error) throw error
      return data as unknown as TenantPage
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['tenant-pages', tenantId] })
      qc.invalidateQueries({ queryKey: ['tenant-page', tenantId, slug] })
      await triggerRevalidation('tenant-pages')
    },
  })
}

export function useDeletePage() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (slug: string) => {
      const { error } = await supabase
        .from('tenant_pages')
        .update({ is_active: false })
        .eq('tenant_id', tenantId!)
        .eq('slug', slug)
        .eq('is_system', false) // guard: cannot delete system pages
      if (error) throw error
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['tenant-pages', tenantId] })
      await triggerRevalidation('tenant-pages')
    },
  })
}
